import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AddHouseModal } from "@features/AddHouseModal/AddHouseModal";
import { useChubbInsuranceModal } from "@features/ChubbInsuranceModal/useChubbInsuranceModal";
import { Button } from "@components/Button";
import { Dropdown } from "@components/Dropdown/Dropdown";
import {
  HouseIcon,
  HousePlusSquareIcon,
  PersonPlusIcon,
  PlusIcon,
  ShieldIcon,
} from "@components/icons";
import { useAddBillModal } from "@hooks/useAddBillModal";
import { useAddPayerModal } from "@hooks/useAddPayerModal";
import { useCurrentJointBillServicePeriodId } from "@hooks/useCurrentJointBillServicePeriodId";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { usePOAModal } from "@hooks/usePOAModal";
import { useModalActions } from "@store/modal/modal";
import {
  useJointBillServicePeriodDataQueries,
  useOverviewQuery,
} from "@services/overview/queries";
import { usePOASignedDocumentMetadataQuery } from "@services/powersOfAttorney/queries";

interface IBillsAddButtonProps {
  className?: string;
}

export const BillsAddButton: React.FC<IBillsAddButtonProps> = ({
  className,
}) => {
  const { t } = useTranslation();
  const { create } = useModalActions();
  const { isPoaEnabled, isChubbInsuranceEnabled } = useFeatureConfig();

  const { data: POAdocumentMetadata } =
    usePOASignedDocumentMetadataQuery(isPoaEnabled);

  const { openAddPayerModal } = useAddPayerModal();
  const { openChubbInsuranceModal } = useChubbInsuranceModal();

  const openAddHouseModal = useCallback(() => {
    create((modalProps) => <AddHouseModal {...modalProps} />);
  }, [create]);

  const { openAddBillModal } = useAddBillModal();
  const { openPOAModal } = usePOAModal();

  const { data: overview, isLoading: isOverviewLoading } = useOverviewQuery();
  const currentPeriodId = useCurrentJointBillServicePeriodId();

  const jointBillPeriodQueries = useJointBillServicePeriodDataQueries(
    (overview?.jointBillServices ?? []).map((jbs) => ({
      jointBillServiceId: jbs.jointBillServiceId,
      periodId: currentPeriodId ?? 0,
    })),
  );

  const isChubbBillInsuranceButtonDisplayed =
    isChubbInsuranceEnabled &&
    !isOverviewLoading &&
    jointBillPeriodQueries.some(
      (query) =>
        query.data?.insuranceStatus === "active" ||
        query.data?.insuranceStatus === "inactive",
    );

  const addActionOptions = useMemo(() => {
    const options = [
      {
        label: t("billsPage.actions.addProvider"),
        onClick: () => openAddBillModal({ shouldSuggestAutomaticPoa: true }),
        icon: PlusIcon,
      },
      {
        label: t("billsPage.actions.addPayer"),
        onClick: openAddPayerModal,
        icon: PersonPlusIcon,
      },
      {
        label: t("billsPage.actions.addHouse"),
        onClick: openAddHouseModal,
        icon: HouseIcon,
      },
    ];

    if (POAdocumentMetadata?.succeeded) {
      options.push({
        label: t("billsPage.actions.powerOfAttorney"),
        onClick: () => openPOAModal({ showExistingConsent: true }),
        icon: HousePlusSquareIcon,
      });
    }

    return options;
  }, [
    POAdocumentMetadata?.succeeded,
    t,
    openAddBillModal,
    openAddHouseModal,
    openAddPayerModal,
    openPOAModal,
  ]);

  const dropdownButton = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (props) => (
      <Button icon={PlusIcon} {...props} className={className}>
        {t("billsPage.actions.add")}
      </Button>
    ),
    [className, t],
  );

  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      <Dropdown options={addActionOptions} buttonAs={dropdownButton} />
      {isChubbBillInsuranceButtonDisplayed && (
        <Button
          className="w-full"
          variant="secondary"
          icon={ShieldIcon}
          onClick={openChubbInsuranceModal}
        >
          {t("billsPage.actions.chubbInsurance")}
        </Button>
      )}
    </div>
  );
};
